<template>
  <Modal 
    :show="modalState.visible && isModalValid" 
    :padding="modalState._options.padding" 
    :showClose="modalState._options.showClose" 
    :closeOnEscape="modalState._options.closeOnEscape"
    :closePosition="modalState._options.closePosition"
    :closeOnBackdropClick="modalState._options.closeOnBackdropClick"
    @update:show="dismiss"
  >
    <component :is="modalState.bodyComponent" />
  </Modal>
</template>

<script>
import { Modal } from '@/pixel/index';
import { h, ref, toRef,watch, toRefs, reactive, onMounted, computed, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';

const modalState = reactive({
  visible: false,
  bodyComponent: null,
  _options: {},
});

export const state = toRefs(modalState)

export async function confirm(title, message, yesLabel, noLabel) {
  const result = await openModal(
    defineAsyncComponent(() => import('./ConfirmModal.vue')),
    {
      title,
      message,
      yesLabel,
      noLabel,
    },
    { showClose: false },
  );
  return result?.result
}

export async function alert(title, message) {
  return openModal(
    defineAsyncComponent(() => import('./AlertModal.vue')),
    {
      title,
      message,
    },
    { showClose: true },
  );
}

export async function openModal(component, renderOptions, options) {
  return new Promise((res, rej) => {
    if (modalState.visible) {
      console.warn('only one modal can be open at a time. existing modal is terminated');
      closeModal({
        action: "evict"
      });
    }

    const opts = {
        showClose: true,
        closeOnEscape: true,
        closeOnBackdropClick: true,
        closePosition: "right",
        padding: true,
      ...options,
    };

    modalState.bodyComponent = (p, c) =>
      h(
        component,
        {
          ...renderOptions,
          close: (result)=>{
            closeModal({
              action: 'event',
              result
            })
          },
        },
        null,
      );

    modalState._options = opts;
    modalState.onClose = res;
    modalState.urlFragment = opts.urlFragment;
    modalState.hasPushedNavigation = false;
    modalState.visible = true;
  });
}

export function closeModal(modalCloseResult) {
  if (!modalState.visible) {
    console.warn('attempting to close modal that is not open');
    return;
  }
  modalState.onClose(modalCloseResult);
  modalState.visible = false;
  modalState.bodyComponent = null;
}

export default {
  name: 'Dialog',
  components: {
    Modal,
  },
  props: {},
  setup(props, ctx) {
    const router = useRouter();

    watch(
      () => modalState.visible,
      async (visible, prevVisible) => {
        const hash = `#${modalState.urlFragment}`;
        if (visible && !prevVisible) {
          ctx.emit('open', modalState);
          if (modalState.urlFragment) {
            if (router.currentRoute.value.hash !== hash){
              await router.push({
                hash,
              });
              modalState.hasPushedNavigation = true;
              window.onpopstate = function(event) {
                close({
                  action: 'back'
                });
              }
            }
          }
        }else if (!visible && prevVisible){
          if (modalState.urlFragment && router.currentRoute.value.hash === hash) {
            if(modalState.hasPushedNavigation){
              window.onpopstate = undefined;
              await router.back();
            }else{
              await router.push({
                hash:'',
              });
            }
          }
        }
      },
    );

    const isModalValid = computed(() => {
      return !!modalState.bodyComponent;
    });

    const dismiss = (result) => {
      close({
        action: "dismiss",
        result
      });
    };

    const close = (modalCloseResult)=>{
      ctx.emit('close', modalCloseResult);
      closeModal(modalCloseResult)
    }

    return {
      modalState,
      dismiss,
      isModalValid,
    };
  },
};
</script>
